import OfflineLogger from '@/utils/offlineLogger';
import { networkService } from '@/services/network.service';
import type { SyncTask } from './sync-queue-manager';

export class SyncRunner {
  public currentAbortController: AbortController | null = null;

  private criticalEntityTimeouts: Record<string, number> = {
    categories: 60000,
    products: 60000,
    shoppingLists: 60000
  };

  async runWithTimeout(task: SyncTask): Promise<{ success: boolean; timedOut: boolean }> {
    if (!networkService.isOnline()) {
      OfflineLogger.warn('SyncRunner', `Skipping task ${task.id} due to offline network`);
      return { success: false, timedOut: false };
    }

    this.currentAbortController = new AbortController();
    const { signal } = this.currentAbortController;

    const timeout = this.criticalEntityTimeouts[task.entityName] ?? task.timeout;

    return new Promise(async resolve => {
      const timeoutId = setTimeout(() => {
        this.currentAbortController?.abort();
        OfflineLogger.warn('SyncRunner', `Task ${task.id} timed out after ${timeout}ms`);
        resolve({ success: false, timedOut: true });
      }, timeout);

      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        OfflineLogger.warn('SyncRunner', `Task ${task.id} aborted`);
        resolve({ success: false, timedOut: false });
      });

      try {
        const success = await this.run(task);
        if (!signal.aborted) {
          clearTimeout(timeoutId);
          resolve({ success, timedOut: false });
        }
      } catch (error) {
        if (!signal.aborted) {
          clearTimeout(timeoutId);
          OfflineLogger.error('SyncRunner', `Task execution error: ${error}`);
        }
        resolve({ success: false, timedOut: false });
      } finally {
        this.currentAbortController = null;
      }
    });
  }

  async run(task: SyncTask): Promise<boolean> {
    const { entityName, userId, operation } = task;

    switch (operation) {
      case 'full-sync':
        return await this.executeFullSync(entityName, userId);

      case 'incremental':
        return await this.executeIncrementalSync(entityName, userId);

      case 'validation':
        return await this.executeValidation(entityName, userId);

      default:
        OfflineLogger.error('SyncRunner', `Unknown operation: ${operation}`);
        return false;
    }
  }

  private async executeFullSync(entityName: string, userId: string): Promise<boolean> {
    try {
      const { syncService } = await import('@/services/sync.service');
      await syncService.syncEntity(entityName, userId);
      return true;
    } catch (error) {
      OfflineLogger.error('SyncRunner.executeFullSync', error);
      return false;
    }
  }

  private async executeIncrementalSync(entityName: string, userId: string): Promise<boolean> {
    try {
      const { syncService } = await import('@/services/sync.service');
      await syncService.syncPendingOperations();
      return true;
    } catch (error) {
      OfflineLogger.error('SyncRunner.executeIncrementalSync', error);
      return false;
    }
  }

  private async executeValidation(entityName: string, userId: string): Promise<boolean> {
    try {
      const { syncValidationService } = await import('./SyncValidationService');
      return await syncValidationService.validateEntityIntegrity(entityName, userId);
    } catch (error) {
      OfflineLogger.error('SyncRunner.executeValidation', error);
      return false;
    }
  }
}

export const syncRunner = new SyncRunner();
